import { ChevronRight, Lock } from 'lucide-react'
import { cloneElement, isValidElement, useMemo } from 'react'
import { Link, useLocation } from 'router-v6'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../../primitives/sidebar'
import { Tooltip } from '../../primitives/tooltip'
import type { AvatarMenuProps } from './components/footer'
import { AppSidebarHeaderDropdown } from './components/header'
import type { IRoute } from './types'

// Helper function to get sidebar groups from routes (excluding footer items)
const getSidebarGroups = (routes: IRoute[] = []) => {
  const groups: Record<string, IRoute[]> = {}
  const ungroupedRoutes: IRoute[] = []
  const pinnedRoutes: IRoute[] = []

  // Process all routes but exclude settings and reports (they go in footer)
  const allRoutes = routes.filter((route) => !route.hidden && !route.notAllowed)

  allRoutes.forEach((route) => {
    if (route.sidebar?.pinnedToBottom) {
      // Pinned to the bottom of the sidebar, above the footer (e.g. Support)
      pinnedRoutes.push(route)
    } else if (route.sidebar?.group) {
      if (!groups[route.sidebar.group]) {
        groups[route.sidebar.group] = []
      }
      groups[route.sidebar.group].push(route)
    } else if (route.sidebar) {
      // Route has sidebar config but no group - add to ungrouped
      ungroupedRoutes.push(route)
    }
  })

  return { groups, ungroupedRoutes, pinnedRoutes }
}

/**
 * Renders a single route item in the sidebar menu
 */
const renderRouteItem = (
  route: IRoute,
  location: ReturnType<typeof useLocation>,
  viewsData: AppSidebarMainProps['viewsData'],
  onConversationsClick?: () => void,
) => {
  const isActive = (() => {
    if (location.pathname === route.path) return true

    // Special handling for conversations route with onClick
    if (route.path === '/conversations' && route.sidebar?.onClick) {
      return false // This uses onClick handler, not direct navigation
    }

    // For conversation routes, be very specific about matching
    if (route.path.startsWith('/conversations/')) {
      // Only match if the pathname exactly matches or is a child path
      return location.pathname === route.path || location.pathname.startsWith(`${route.path}/`)
    }

    if (location.pathname.startsWith(`${route.path}/`)) {
      return true
    }

    return false
  })()

  const badgeCount = route.sidebar?.badgeKey
    ? viewsData?.[route.sidebar.badgeKey as keyof typeof viewsData]
    : null

  // Enterprise-locked item (e.g. WhatsApp / Verify on non-Enterprise plans):
  // ghosted label + trailing lock icon with a hover tooltip; clicking opens the
  // upgrade flow (onLockedClick) instead of navigating into the feature.
  if (route.locked) {
    return (
      <SidebarMenuItem key={route.path}>
        <Tooltip content={route.lockedTooltip} delayDuration={300}>
          <SidebarMenuButton
            isActive={false}
            onClick={route.onLockedClick}
            className="opacity-50"
            data-testid={`sidebar-locked-item-${route.path.replace(/\//g, '')}`}
          >
            {route.icon &&
              (isValidElement(route.icon)
                ? cloneElement(route.icon, {
                    strokeWidth: 1.5,
                    className: 'w-4 h-4',
                  } as { strokeWidth: number; className: string })
                : route.icon)}
            <span className="flex-1">{route.title}</span>
            <span className="ml-auto flex items-center">
              <Lock className="h-3 w-3 shrink-0" strokeWidth={2.5} />
            </span>
          </SidebarMenuButton>
        </Tooltip>
      </SidebarMenuItem>
    )
  }

  // Special handling for items with onClick (like Conversations)
  if (route.sidebar?.onClick === 'onConversationsClick') {
    return (
      <SidebarMenuItem key={route.path} className="group/conversations-item">
        <SidebarMenuButton
          tooltip={String(route.title)}
          isActive={isActive}
          onClick={onConversationsClick}
        >
          {route.icon &&
            (isValidElement(route.icon)
              ? cloneElement(route.icon, {
                  strokeWidth: isActive ? 2 : 1.5,
                  className: 'w-4 h-4',
                } as { strokeWidth: number; className: string })
              : route.icon)}
          <div className="flex items-center justify-between w-full">
            <span>{route.title}</span>
            {route.sidebar?.hasChevron && (
              <ChevronRight
                className="size-4 opacity-0 group-hover/conversations-item:opacity-100 transition-opacity duration-200"
                strokeWidth={1.5}
              />
            )}
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem key={route.path}>
      <SidebarMenuButton tooltip={String(route.title)} isActive={isActive} asChild>
        <Link to={route.path} className="flex items-center gap-2 w-full">
          {route.icon &&
            (isValidElement(route.icon)
              ? cloneElement(route.icon, {
                  strokeWidth: isActive ? 2 : 1.5,
                  className: 'w-4 h-4',
                } as { strokeWidth: number; className: string })
              : route.icon)}
          <span className="flex-1">{route.title}</span>
          {badgeCount && typeof badgeCount === 'number' && badgeCount > 0 ? (
            <span className="text-xs bg-sidebar-accent text-sidebar-accent-foreground px-1.5 py-0.5 rounded">
              {badgeCount}
            </span>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

interface AppSidebarMainProps extends React.ComponentProps<typeof Sidebar> {
  avatarProps?: AvatarMenuProps
  routes?: IRoute[]
  viewsData?: {
    favourites: Array<{ name: string; uuid: string }>
    publicViews: Array<{ name: string; uuid: string }>
    hasViews: boolean
    // Conversation counts and data
    activeConversations: number
    invitedContacts: number
    tasksData: number
    myTasksData: number
    unassignedTasks: number
    exceptionsTasks: number
    // User permissions
    isHumanAgent?: boolean
    hasLiveConversationPermission: boolean
    // Functions
    favouriteView: (params: { uuid: string; favourite: boolean }) => void
    authDetails: { name?: string } | null
  }
  onSettingsClick: () => void
  onConversationsClick: () => void
  isCollapsible: boolean
  handleOrganizationClick: (org: NonNullable<AvatarMenuProps['organizations']>[number]) => void
  externalState?: 'collapsed' | 'expanded'
  onCreateOrganization?: () => void
  onLogoClick?: () => void
  // Extra app-provided item(s) rendered at the top of the footer menu (e.g. promo banners).
  footerExtra?: React.ReactNode
}

// Only show these groups in sidebar
const ALLOWED_GROUPS = ['build', 'deploy', 'monitor']

export const AppSidebarMain: React.FC<AppSidebarMainProps> = ({
  externalState,
  avatarProps,
  routes,
  viewsData,
  onSettingsClick,
  onConversationsClick,
  isCollapsible,
  handleOrganizationClick,
  onCreateOrganization,
  onLogoClick,
  footerExtra,
  ...props
}) => {
  const location = useLocation()
  const { state: sidebarState } = useSidebar()
  const state = externalState || sidebarState
  const isCollapsed = state === 'collapsed'

  // Get sidebar groups from routes and filter to only allowed groups
  const { sidebarGroups, ungroupedRoutes, pinnedRoutes } = useMemo(() => {
    const { groups, ungroupedRoutes, pinnedRoutes } = getSidebarGroups(routes)
    // Filter to only show BUILD, DEPLOY, MONITOR groups
    const filteredGroups = Object.entries(groups)
      .filter(([groupName]) => ALLOWED_GROUPS.includes(groupName))
      .sort(([a], [b]) => ALLOWED_GROUPS.indexOf(a) - ALLOWED_GROUPS.indexOf(b))
    return { sidebarGroups: filteredGroups, ungroupedRoutes, pinnedRoutes }
  }, [routes])

  // Map organizations for header dropdown
  const organizations = useMemo(
    () =>
      (avatarProps?.organizations || []).map((org) => ({
        id: org.org_uuid,
        name: org.org_name,
        region: org.region,
      })),
    [avatarProps?.organizations],
  )

  return (
    <Sidebar collapsible={isCollapsible ? 'icon' : 'none'} className="group" {...props}>
      <AppSidebarHeaderDropdown
        currentOrgUuid={avatarProps?.orgUuid || ''}
        organizations={organizations}
        onOrganizationChange={(orgId) => {
          const org = avatarProps?.organizations?.find((o) => o.org_uuid === orgId)
          if (org) handleOrganizationClick(org)
        }}
        onCreateOrganization={onCreateOrganization}
        onLogoClick={onLogoClick}
        isCollapsed={isCollapsed}
      />

      <SidebarContent>
        {/* Ungrouped routes (e.g. Home). Search lives in the top nav bar, not here. */}
        {/* px-2 matches the grouped sections below so Home aligns with their items. */}
        <SidebarGroup>
          <SidebarGroupContent className="px-2">
            <SidebarMenu>
              {ungroupedRoutes.map((route) =>
                renderRouteItem(route, location, viewsData, onConversationsClick),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* BUILD, DEPLOY, MONITOR groups */}
        {sidebarGroups.map(([groupName, groupRoutes]) => {
          const groupLabel = groupName.toUpperCase()

          // Render all route items and filter out null values
          const renderedItems = groupRoutes
            .map((route) => renderRouteItem(route, location, viewsData, onConversationsClick))
            .filter(Boolean)

          if (renderedItems.length === 0) return null

          return (
            <SidebarGroup key={groupName}>
              <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>
              <SidebarGroupContent className="px-2">
                <SidebarMenu>{renderedItems}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      {/* Bottom-pinned routes (e.g. Support) — rendered in the footer, OUTSIDE the scrollable
          SidebarContent, so they sit at the bottom of the sidebar below every nav group. (mt-auto
          inside SidebarContent is a no-op: its content wrapper is shrink-0, not full height.) */}
      {/* footerExtra (promo card) is dropped in the collapsed rail — the org picker
          up top already carries the flag, and an icon-only card has no room to say anything. */}
      {(pinnedRoutes.length > 0 || (footerExtra && !isCollapsed)) && (
        <SidebarFooter className="border-t border-sidebar-border">
          <SidebarMenu>
            {!isCollapsed && footerExtra}
            {pinnedRoutes.map((route) => {
              const isActive =
                location.pathname === route.path || location.pathname.startsWith(`${route.path}/`)
              return (
                <SidebarMenuItem key={route.path}>
                  {/* Pinned items (e.g. Support) get a contained, button-like treatment so they
                      read as a distinct action rather than blending into the nav list. */}
                  <SidebarMenuButton
                    tooltip={String(route.title)}
                    isActive={isActive}
                    asChild
                    className="border border-sidebar-border bg-background shadow-sm hover:bg-hv1"
                  >
                    <Link to={route.path} className="flex items-center gap-2 w-full">
                      {route.icon &&
                        isValidElement(route.icon) &&
                        cloneElement(route.icon, {
                          className: 'w-4 h-4 text-primary',
                          strokeWidth: 2,
                        } as { className: string; strokeWidth: number })}
                      <span className="flex-1 font-medium">{route.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarFooter>
      )}
    </Sidebar>
  )
}
